import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Alert, TouchableOpacity, Modal, Platform } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import ModernButton from '../components/ModernButton';
import ModernInput from '../components/ModernInput';
import BusinessCategoryPicker from '../components/BusinessCategoryPicker';
import { COUNTRIES, getStatesForCountry } from '../constants/states';
import { geocodingService } from '../services/geocodingService';
import { BUSINESS_HOURS_AM_OPTIONS, BUSINESS_HOURS_PM_OPTIONS, formatTime24To12 } from '../utils/businessHoursUtils';

interface CompanySetupScreenProps {
  onComplete: (companyData: CompanyData) => void;
  onBack: () => void;
}

interface BusinessHours {
  [day: string]: {
    isOpen: boolean;
    openTime?: string; // Format: "HH:mm" (e.g., "09:00")
    closeTime?: string; // Format: "HH:mm" (e.g., "17:00")
  };
}

interface CompanyData {
  companyName: string;
  category: string;
  categoryId: string;
  address: string; // Street address
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  website: string;
  description: string;
  businessHours: BusinessHours;
  appointmentDuration: number; // Duration between appointments in minutes
}

const CompanySetupScreen: React.FC<CompanySetupScreenProps> = ({
  onComplete,
  onBack,
}) => {
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  const [formData, setFormData] = useState<CompanyData>({
    companyName: '',
    category: '',
    categoryId: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'USA', // Default to USA
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    website: '',
    description: '',
    businessHours: {
      Monday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Friday: { isOpen: true, openTime: '09:00', closeTime: '17:00' },
      Saturday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
      Sunday: { isOpen: false, openTime: '09:00', closeTime: '17:00' },
    },
    appointmentDuration: 30, // Default 30 minutes
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [timePickerModal, setTimePickerModal] = useState<{
    visible: boolean;
    day: string | null;
    field: 'openTime' | 'closeTime' | null;
  }>({ visible: false, day: null, field: null });

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.categoryId.trim()) {
      newErrors.category = 'Business category is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Street address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.zipcode.trim()) {
      newErrors.zipcode = 'Zipcode is required';
    } else if (!geocodingService.isValidZipcode(formData.zipcode, formData.country)) {
      newErrors.zipcode =
        'Enter a valid US ZIP (5 digits), Canadian postal code (A1A 1A1), or India PIN (6 digits).';
    } else {
      // Auto-detect country from zipcode format
      const trimmedZ = formData.zipcode.trim();
      const isUSZipcode = /^\d{5}$/.test(trimmedZ);
      const isCanadianPostal = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(trimmedZ);
      const isIndiaPin = /^\d{6}$/.test(trimmedZ);

      if (isUSZipcode && formData.country !== 'USA') {
        setFormData(prev => ({ ...prev, country: 'USA', state: '' }));
      } else if (isCanadianPostal && formData.country !== 'Canada') {
        setFormData(prev => ({ ...prev, country: 'Canada', state: '' }));
      } else if (isIndiaPin && formData.country !== 'India') {
        setFormData(prev => ({ ...prev, country: 'India', state: '' }));
      }
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State/Province is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Validate business hours - at least one day must be open
    const hasOpenDay = Object.values(formData.businessHours).some(day => day.isOpen);
    if (!hasOpenDay) {
      newErrors.businessHours = 'At least one day must be open';
    }
    
    // Validate business hours times for open days
    daysOfWeek.forEach(day => {
      const dayHours = formData.businessHours[day];
      if (dayHours.isOpen) {
        if (!dayHours.openTime || !dayHours.closeTime) {
          newErrors[`${day}Hours`] = `${day} hours are required when open`;
        } else if (dayHours.openTime >= dayHours.closeTime) {
          newErrors[`${day}Hours`] = `${day} close time must be after open time`;
        }
      }
    });

    // Validate appointment duration
    if (!formData.appointmentDuration || formData.appointmentDuration < 15) {
      newErrors.appointmentDuration = 'Appointment duration must be at least 15 minutes';
    } else if (formData.appointmentDuration > 480) {
      newErrors.appointmentDuration = 'Appointment duration cannot exceed 8 hours';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onComplete(formData);
    } else {
      Alert.alert('Validation Error', 'Please fill in all required fields correctly.');
    }
  };

  const updateField = (field: keyof CompanyData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <ScrollView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Company Setup</Text>
        <Text style={styles.subtitle}>Tell us about your business</Text>
      </View>

      <View style={styles.formCard}>
        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            Company Name <Text style={styles.required}>*</Text>
          </Text>
          <AppTextInput
            style={styles.input}
            placeholder=""
            value={formData.companyName}
            onChangeText={(value) => updateField('companyName', value)}
            autoCapitalize="words"
          />
          {errors.companyName ? <Text style={styles.errorText}>{errors.companyName}</Text> : null}
        </View>

        {/* Business Category Selection */}
        <View style={styles.inputContainer}>
          <BusinessCategoryPicker
            label="Business Category *"
            categoryId={formData.categoryId}
            error={errors.category}
            onSelect={(categoryId, categoryName) => {
              updateField('categoryId', categoryId);
              updateField('category', categoryName);
            }}
          />
        </View>

        <ModernInput
          label="Street Address *"
          placeholder=""
          value={formData.address}
          onChangeText={(value) => updateField('address', value)}
          error={errors.address}
        />

        <View style={styles.inputContainer}>
          <Text style={styles.label}>City *</Text>
          <AppTextInput
            style={[styles.input, errors.city && styles.inputError]}
            placeholder=""
            value={formData.city}
            onChangeText={(value) => updateField('city', value)}
            autoCapitalize="words"
          />
          {errors.city && <Text style={styles.errorText}>{errors.city}</Text>}
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            Country <Text style={styles.required}>*</Text>
          </Text>
          <TouchableOpacity
            style={[styles.input, styles.dropdownButton, errors.country && styles.inputError]}
            onPress={() => setShowCountryDropdown(true)}
          >
            <Text style={[styles.dropdownButtonText, !formData.country && styles.placeholderText]}>
              {formData.country 
                ? COUNTRIES.find(c => c.code === formData.country)?.name || formData.country
                : 'Select Country'}
            </Text>
            <Text style={styles.dropdownArrow}>▼</Text>
          </TouchableOpacity>
          {errors.country && <Text style={styles.errorText}>{errors.country}</Text>}
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            State/Province <Text style={styles.required}>*</Text>
          </Text>
          <TouchableOpacity
            style={[styles.input, styles.dropdownButton, errors.state && styles.inputError]}
            onPress={() => {
              if (!formData.country) {
                Alert.alert('Select Country First', 'Please select a country before choosing a state/province.');
                return;
              }
              setShowStateDropdown(true);
            }}
          >
            <Text style={[styles.dropdownButtonText, !formData.state && styles.placeholderText]}>
              {formData.state || (formData.country === 'Canada' ? 'Select Province' : 'Select State')}
            </Text>
            <Text style={styles.dropdownArrow}>▼</Text>
          </TouchableOpacity>
          {errors.state && <Text style={styles.errorText}>{errors.state}</Text>}
        </View>

        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            Zipcode <Text style={styles.required}>*</Text>
          </Text>
          <AppTextInput
            style={[styles.input, errors.zipcode && styles.inputError]}
            placeholder=""
            value={formData.zipcode}
            onChangeText={(value) => updateField('zipcode', value)}
            autoCapitalize="characters"
            maxLength={7}
          />
          {errors.zipcode && <Text style={styles.errorText}>{errors.zipcode}</Text>}
        </View>

        <ModernInput
          label="Phone Number *"
          placeholder=""
          value={formData.phone}
          onChangeText={(value) => updateField('phone', value)}
          error={errors.phone}
          keyboardType="phone-pad"
        />

        <ModernInput
          label="Email *"
          placeholder=""
          value={formData.email}
          onChangeText={(value) => updateField('email', value)}
          error={errors.email}
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <ModernInput
          label="Password *"
          placeholder=""
          value={formData.password}
          onChangeText={(value) => updateField('password', typeof value === 'string' ? value : '')}
          error={errors.password}
          secureTextEntry
          textContentType="oneTimeCode"
          autoComplete="off"
        />

        <ModernInput
          label="Confirm Password *"
          placeholder=""
          value={formData.confirmPassword}
          onChangeText={(value) => updateField('confirmPassword', typeof value === 'string' ? value : '')}
          error={errors.confirmPassword}
          secureTextEntry
          textContentType="oneTimeCode"
          autoComplete="off"
        />

        <ModernInput
          label="Website"
          placeholder=""
          value={formData.website}
          onChangeText={(value) => updateField('website', value)}
        />

        <ModernInput
          label="Description"
          placeholder=""
          value={formData.description}
          onChangeText={(value) => updateField('description', value)}
          multiline
          numberOfLines={4}
        />

        {/* Business Hours Section */}
        <View style={styles.sectionDivider} />
        <Text style={styles.sectionTitle}>Business Hours *</Text>
        <Text style={styles.sectionSubtitle}>Set your operating hours for each day of the week</Text>
        
        {daysOfWeek.map((day) => (
          <View key={day} style={styles.hoursRow}>
            <View style={styles.hoursDayContainer}>
              <Text style={styles.hoursDayLabel}>{day}</Text>
              <TouchableOpacity
                style={[
                  styles.toggleSwitch,
                  formData.businessHours[day].isOpen && styles.toggleSwitchActive
                ]}
                onPress={() => {
                  setFormData(prev => ({
                    ...prev,
                    businessHours: {
                      ...prev.businessHours,
                      [day]: {
                        ...prev.businessHours[day],
                        isOpen: !prev.businessHours[day].isOpen
                      }
                    }
                  }));
                }}
              >
                <Text style={styles.toggleSwitchText}>
                  {formData.businessHours[day].isOpen ? 'Open' : 'Closed'}
                </Text>
              </TouchableOpacity>
            </View>
            {formData.businessHours[day].isOpen && (
              <View style={styles.hoursTimeContainer}>
                <TouchableOpacity
                  style={styles.timeInput}
                  onPress={() => setTimePickerModal({ visible: true, day, field: 'openTime' })}
                >
                  <Text style={styles.timeInputText}>
                    {formatTime24To12(formData.businessHours[day].openTime || '09:00')}
                  </Text>
                  <Text style={styles.timeInputChevron}>▼</Text>
                </TouchableOpacity>
                <Text style={styles.hoursSeparator}>to</Text>
                <TouchableOpacity
                  style={styles.timeInput}
                  onPress={() => setTimePickerModal({ visible: true, day, field: 'closeTime' })}
                >
                  <Text style={styles.timeInputText}>
                    {formatTime24To12(formData.businessHours[day].closeTime || '17:00')}
                  </Text>
                  <Text style={styles.timeInputChevron}>▼</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ))}
        {errors.businessHours && <Text style={styles.errorText}>{errors.businessHours}</Text>}

        {/* Appointment Duration */}
        <View style={styles.sectionDivider} />
        <View style={styles.inputContainer}>
          <Text style={styles.label}>
            Duration Between Appointments (minutes) <Text style={styles.required}>*</Text>
          </Text>
          <AppTextInput
            style={[styles.input, errors.appointmentDuration && styles.inputError]}
            placeholder="30"
            value={formData.appointmentDuration?.toString() || ''}
            onChangeText={(value) => {
              const numValue = parseInt(value) || 0;
              updateField('appointmentDuration', numValue.toString());
            }}
            keyboardType="numeric"
          />
          <Text style={styles.helperText}>
            Minimum time between appointments (e.g., 15, 30, 60 minutes)
          </Text>
          {errors.appointmentDuration && <Text style={styles.errorText}>{errors.appointmentDuration}</Text>}
        </View>
      </View>

      {/* Country Dropdown Modal */}
      <Modal
        visible={showCountryDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowCountryDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCountryDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>Select Country</Text>
              <TouchableOpacity onPress={() => setShowCountryDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {COUNTRIES.map((country) => (
                <TouchableOpacity
                  key={country.code}
                  style={styles.dropdownOption}
                  onPress={() => {
                    // Clear state when country changes
                    setFormData(prev => {
                      const newData = { ...prev, country: country.code };
                      if (prev.country !== country.code) {
                        newData.state = '';
                      }
                      return newData;
                    });
                    setShowCountryDropdown(false);
                  }}
                >
                  <Text style={styles.dropdownOptionText}>{country.name}</Text>
                  {formData.country === country.code && (
                    <Text style={styles.dropdownCheckmark}>✓</Text>
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Dropdown Modal */}
      <Modal
        visible={showStateDropdown}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowStateDropdown(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateDropdown(false)}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>
                Select {formData.country === 'Canada' ? 'Province' : 'State'}
              </Text>
              <TouchableOpacity onPress={() => setShowStateDropdown(false)}>
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              {getStatesForCountry(formData.country).length === 0 ? (
                <View style={styles.dropdownOption}>
                  <Text style={styles.dropdownOptionText}>
                    Please select a country first
                  </Text>
                </View>
              ) : (
                getStatesForCountry(formData.country).map((state) => (
                  <TouchableOpacity
                    key={state.code}
                    style={styles.dropdownOption}
                    onPress={() => {
                      updateField('state', state.code);
                      setShowStateDropdown(false);
                    }}
                  >
                    <Text style={styles.dropdownOptionText}>
                      {state.code} - {state.name}
                    </Text>
                    {formData.state === state.code && (
                      <Text style={styles.dropdownCheckmark}>✓</Text>
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Business Hours Time Picker Modal */}
      <Modal
        visible={timePickerModal.visible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setTimePickerModal({ visible: false, day: null, field: null })}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setTimePickerModal({ visible: false, day: null, field: null })}
        >
          <View style={styles.dropdownModal} onStartShouldSetResponder={() => true}>
            <View style={styles.dropdownHeader}>
              <Text style={styles.dropdownTitle}>
                {timePickerModal.field === 'openTime' ? 'Open' : 'Close'} time
                {timePickerModal.day ? ` (${timePickerModal.day})` : ''}
              </Text>
              <TouchableOpacity
                onPress={() => setTimePickerModal({ visible: false, day: null, field: null })}
              >
                <Text style={styles.dropdownClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.dropdownList}>
              <Text style={styles.dropdownSectionHeader}>AM</Text>
              {BUSINESS_HOURS_AM_OPTIONS.map((opt) => {
                const day = timePickerModal.day;
                const field = timePickerModal.field;
                const current =
                  day && field ? formData.businessHours[day]?.[field] || '' : '';
                const selected = current === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    style={styles.dropdownOption}
                    onPress={() => {
                      if (day && field) {
                        setFormData((prev) => ({
                          ...prev,
                          businessHours: {
                            ...prev.businessHours,
                            [day]: {
                              ...prev.businessHours[day],
                              [field]: opt.value,
                            },
                          },
                        }));
                      }
                      setTimePickerModal({ visible: false, day: null, field: null });
                    }}
                  >
                    <Text style={styles.dropdownOptionText}>{opt.label}</Text>
                    {selected && <Text style={styles.dropdownCheckmark}>✓</Text>}
                  </TouchableOpacity>
                );
              })}
              <Text style={styles.dropdownSectionHeader}>PM</Text>
              {BUSINESS_HOURS_PM_OPTIONS.map((opt) => {
                const day = timePickerModal.day;
                const field = timePickerModal.field;
                const current =
                  day && field ? formData.businessHours[day]?.[field] || '' : '';
                const selected = current === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.value}
                    style={styles.dropdownOption}
                    onPress={() => {
                      if (day && field) {
                        setFormData((prev) => ({
                          ...prev,
                          businessHours: {
                            ...prev.businessHours,
                            [day]: {
                              ...prev.businessHours[day],
                              [field]: opt.value,
                            },
                          },
                        }));
                      }
                      setTimePickerModal({ visible: false, day: null, field: null });
                    }}
                  >
                    <Text style={styles.dropdownOptionText}>{opt.label}</Text>
                    {selected && <Text style={styles.dropdownCheckmark}>✓</Text>}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <View style={styles.buttonContainer}>
        <ModernButton
          title="Back"
          variant="outline"
          onPress={onBack}
          style={styles.backButton}
        />
        <ModernButton
          title="Continue"
          variant="primary"
          onPress={handleSubmit}
          style={styles.continueButton}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  header: {
    padding: 20,
    paddingTop: 60,
    backgroundColor: '#007AFF',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  formCard: {
    margin: 20,
    marginTop: -10,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 8,
  },
  categorySelector: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  errorBorder: {
    borderColor: '#FF3B30',
    borderWidth: 2,
  },
  selectedCategoryText: {
    fontSize: 16,
    color: '#1C1C1E',
    fontWeight: '500',
  },
  placeholderText: {
    fontSize: 16,
    color: '#999',
  },
  dropdownIcon: {
    fontSize: 14,
    color: '#8E8E93',
  },
  categoryDropdown: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#E1E5E9',
    borderRadius: 8,
    marginTop: 8,
    maxHeight: 350,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
  },
  dropdownSearch: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  searchIcon: {
    fontSize: 16,
    marginRight: 8,
    opacity: 0.6,
  },
  dropdownSearchInput: {
    flex: 1,
    fontSize: 14,
    color: '#1C1C1E',
    paddingVertical: 4,
  },
  categoryList: {
    maxHeight: 280,
  },
  categoryOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
  },
  categoryOptionIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  categoryOptionText: {
    flex: 1,
  },
  categoryOptionName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    marginBottom: 2,
  },
  categoryOptionDesc: {
    fontSize: 12,
    color: '#8E8E93',
    lineHeight: 16,
  },
  input: {
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1C1C1E',
  },
  inputError: {
    borderColor: '#FF3B30',
  },
  required: {
    color: '#FF3B30',
  },
  errorText: {
    color: '#FF3B30',
    fontSize: 14,
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 12,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#8E8E93',
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  dropdownModal: {
    backgroundColor: 'white',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
  },
  dropdownHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e5e9',
  },
  dropdownTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  dropdownClose: {
    fontSize: 24,
    color: '#8E8E93',
    padding: 5,
  },
  dropdownList: {
    maxHeight: 400,
  },
  dropdownSectionHeader: {
    fontSize: 14,
    fontWeight: '700',
    color: '#8E8E93',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
    backgroundColor: '#F2F2F7',
  },
  dropdownOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: '#1C1C1E',
    flex: 1,
  },
  dropdownCheckmark: {
    fontSize: 18,
    color: '#007AFF',
    fontWeight: 'bold',
    marginLeft: 10,
  },
  buttonContainer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  backButton: {
    flex: 1,
  },
  continueButton: {
    flex: 2,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: '#E1E5E9',
    marginVertical: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 16,
  },
  hoursRow: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  hoursDayContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  hoursDayLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1C1C1E',
    flex: 1,
  },
  toggleSwitch: {
    backgroundColor: '#E1E5E9',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    minWidth: 70,
    alignItems: 'center',
  },
  toggleSwitchActive: {
    backgroundColor: '#34C759',
  },
  toggleSwitchText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFF',
  },
  hoursTimeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  timeInput: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#C7C7CC',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  timeInputText: {
    fontSize: 16,
    color: '#1C1C1E',
  },
  timeInputChevron: {
    fontSize: 10,
    color: '#8E8E93',
  },
  hoursSeparator: {
    marginHorizontal: 12,
    fontSize: 16,
    color: '#8E8E93',
  },
  helperText: {
    fontSize: 12,
    color: '#8E8E93',
    marginTop: 4,
  },
});

export default CompanySetupScreen;